import React, { Fragment, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom' // temp commented navigate
import { toast } from 'react-toastify'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import CatalogueAPI from 'api/orbitV2/CatalogueAPI'

import OrderAPI from '../../../../api/orbitV1/OrderAPI'
import OrgAPI from '../../../../api/orbitV1/OrgAPI'
import PageLayout from '../../../../common/Layout/Page/PageLayout'
import { ERR_UNEXPECTED } from '../../../../constants/errors'
import { PAYMENT_METHODS } from '../../../../constants/payment'
import OrbitSession from '../../../../hooks/useCookies'
import { PaymentModal } from '../../../Payment/components'

import { OrderPayment, OrderReview, PlanOptions } from './components/index'

// type PlanType = {
//   _id: string
//   productName: string
//   basePlan: {
//     price: {
//       prefix: string
//       value: string
//       currency: string
//     }
//   }
//   addons: {
//     addonName: string
//     data: number
//     voice: number
//     sms: number
//     price: {
//       prefix: string
//       value: string
//       currency: string
//     }
//   }[]
// }

export default function NewOrder() {
  const orgName = OrbitSession.get('orgName')
  const orgId = OrbitSession.get('orgId')
  const email = OrbitSession.get('email')
  const userId = OrbitSession.get('_id')
  const contactName = OrbitSession.get('userName')
  const contactNumber = OrbitSession.get('contactNumber')
  const PLANS_LIMIT = 5

  const [page, setPage] = useState(0)

  const [plans, setPlans] = useState([
    {
      _id: '',
      productName: '',
      baseplan: {
        price: { prefix: '$', value: '0', currency: 'SGD' },
        data: 0,
        voice: 0,
        sms: 0
      },
      addons: [
        {
          addonName: '',
          data: 0,
          voice: 0,
          sms: 0,
          price: { prefix: '$', value: '0', currency: 'SGD' }
        }
      ]
    }
  ])
  const [planId, setPlanId] = useState(0)
  const addOnPrice = 0
  const [totalPrice, setTotalPrice] = useState(0)
  const [numberOfSims, setNumberOfSims] = useState(1)
  const numberOfSimsOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  const [paymentMethod, setPaymentMethod] = useState(
    PAYMENT_METHODS.BANK_TRANSFER
  )

  const [uen, setUen] = useState('')
  const [deliveryAddress, setDelAddr] = useState({})
  const [billingAddress, setBillAddr] = useState({})
  const [product, setProduct] = useState([])
  const [unexpectedError, setUnexpectedError] = useState('')

  // Dialog states

  const [isDialogOpen, setDialogOpen] = useState(false)
  const [backdropLoader, setBackdropLoader] = useState(false)
  const [hasCreated, setHasCreated] = useState(false)
  const [orderMetaInfo, setOrderMetaInfo] = useState({})

  useEffect(() => {
    setTotalPrice(
      plans[planId].baseplan.price.value * numberOfSims + addOnPrice
    )
  }, [plans, planId, numberOfSims])

  // Capitalise only first letter of each word assuming input is all caps

  const capitaliseFirst = (str) => {
    if (str?.length > 0) {
      const strArray = str.split(' ')
      let result = ''
      for (const word of strArray) {
        result = result + ' ' + word[0] + word.slice(1).toLowerCase()
      }
      return result.slice(1)
    }
    return str
  }

  const { data: retrieveProducts, error: retrieveProductsError } = useQuery(
    ['getProducts'],
    async () => {
      const { data } = await CatalogueAPI.getAllProducts(PLANS_LIMIT)
      return data
    }
  )

  if (retrieveProductsError) {
    toast.error('Error retrieving products. Please try again later!')
  }

  useEffect(() => {
    if (retrieveProducts) {
      setPlans(retrieveProducts)
    }
  }, [retrieveProducts])

  const { data: retrieveOrg, error: retrieveOrgError } = useQuery(
    ['getOrg', orgId],
    async () => {
      const { data } = await OrgAPI.getOrgDetails(orgId)
      return data
    }
  )

  if (retrieveOrgError) {
    toast.error(
      'Error retrieving organization details. Please try again later!'
    )
  }

  useEffect(() => {
    if (retrieveOrg) {
      const company = retrieveOrg[0]
      setUen(company?.uen)
      setBillAddr(company?.billingAddress)
      setDelAddr(company?.deliveryAddress)
      setProduct(company?.product)
    }
  }, [retrieveOrg])

  const handleCreate = async () => {
    const orderDetails = {
      // do not send newUser var so that no new order is created
      contactName: OrbitSession.get('userName'),
      orgId,
      orgName,
      type: 'order',
      email,
      plan: plans[planId]._id,
      planName: plans[planId].name,
      numberOfSims: parseInt(numberOfSims),
      price: totalPrice,
      paymentMethod,
      itemName: `${numberOfSims} ${plans[planId].productName} SIMs`, // for credit card
      userId
    }
    try {
      if (!hasCreated) {
        const metaInfo = await OrderAPI.createOrder(orderDetails)
        setHasCreated(true)
        setOrderMetaInfo(metaInfo)
      }
    } catch (error) {
      toast.error('Error creating order.')
    }
  }

  const handleSubmit = async () => {
    setUnexpectedError('')

    // Call backend to create stripe client session for payment
    try {
      if (paymentMethod === PAYMENT_METHODS.BANK_TRANSFER) {
        setDialogOpen(false)
        setBackdropLoader(false)
        // setPaymentPageNo(0)
        goNextPage()
      }
    } catch (error) {
      setUnexpectedError(ERR_UNEXPECTED)
      toast.error('Error submitting payment!')
    }
  }

  const goNextPage = () => setPage(page + 1)
  const goPreviousPage = () => setPage(page - 1)

  const SELECT_PLAN = (
    <PlanOptions
      planId={planId}
      setPlanId={setPlanId}
      plans={plans}
      product={product}
      numberOfSims={numberOfSims}
      setNumberOfSims={setNumberOfSims}
      numberOfSimsOptions={numberOfSimsOptions}
      goNextPage={goNextPage}
    />
  )

  /* Review Order Page */
  const reviewOrderDetails = {
    'Company Name': orgName,
    'Unique Entity Number (UEN)': uen,
    "Contact Person's Name": contactName,
    'Contact Number': `+65 ${contactNumber}`,
    Email: email
  }

  const reviewOrderDelivery = {
    'Delivery Address': deliveryAddress,
    'Billing Address': billingAddress
  }

  const REVIEW_ORDER = (
    <OrderReview
      planId={planId}
      plans={plans}
      numberOfSims={numberOfSims}
      totalPrice={totalPrice}
      addOnPrice={addOnPrice}
      unexpectedError={unexpectedError}
      nextHandler={goNextPage}
      goPreviousPage={goPreviousPage}
      setDialogOpen={setDialogOpen}
    >
      {Object.entries(reviewOrderDetails).map(([label, value]) => {
        return (
          <Fragment key={label}>
            <Typography sx={{ color: 'neutrals.500' }}>{label}</Typography>
            <Typography> {value}</Typography>
          </Fragment>
        )
      })}

      <Typography sx={{ mt: '30px', color: 'primary.light' }}>
        Delivery and Billing Details
      </Typography>

      {Object.entries(reviewOrderDelivery).map(([label, value]) => {
        return (
          <Fragment key={label}>
            <Typography sx={{ marginBottom: 0, color: 'neutrals.500' }}>
              {label}
            </Typography>
            <Typography sx={{ marginTop: 0, marginBottom: '16px' }}>
              {value.blockNo} {capitaliseFirst(value.streetName)}
              <br />
              {value.unitNo ? value.unitNo + ' ' : ''}
              {value.buildingName !== 'NIL'
                ? capitaliseFirst(value.buildingName) + ', '
                : ''}
              {value.postalCode}
            </Typography>
          </Fragment>
        )
      })}
    </OrderReview>
  )

  const ORDER_PAYMENT = (
    <OrderPayment
      planId={planId}
      plans={plans}
      numberOfSims={numberOfSims}
      totalPrice={totalPrice}
      addOnPrice={addOnPrice}
      handleCreate={handleCreate}
      unexpectedError={unexpectedError}
      goPreviousPage={goPreviousPage}
      setDialogOpen={setDialogOpen}
    />
  )

  const SUBMITTED = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: `0px 24px`
      }}
    >
      <Box sx={{ width: '60%', textAlign: 'center', marginBottom: '48px' }}>
        <Typography variant='h4' sx={{ marginBottom: '48px' }}>
          Thank you for placing an order with Circles.Life Orbit
        </Typography>
        <Typography variant='h6'>
          Your order is currently being processed. A confirmation email will be
          sent to the provided email.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '60%'
        }}
      >
        <Button
          id='viewOrdersBtn'
          variant='filled'
          as={RouterLink}
          to='/app/orders'
          width='100%'
        >
          View Orders
        </Button>
      </Box>
    </Box>
  )

  const pages = [SELECT_PLAN, REVIEW_ORDER, ORDER_PAYMENT, SUBMITTED] // Hide payment page

  return (
    <PageLayout header='Buy New SIMs'>
      <Backdrop
        sx={{ color: '#fff', zIndex: (_theme) => _theme.zIndex.drawer + 1 }}
        open={backdropLoader}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          padding: '32px 0'
        }}
      >
        {page < pages.length - 1 && (
          <Box
            sx={{
              marginBottom: '16px',
              padding: '0 24px'
            }}
          >
            <Stepper activeStep={page} sx={{ width: '100%', margin: 0 }}>
              {[...Array(pages.length - 1).keys()].map((step) => {
                return (
                  <Step key={step + 1}>
                    <StepLabel>{}</StepLabel>
                  </Step>
                )
              })}
            </Stepper>
          </Box>
        )}
        {pages[page]}
      </Box>
      <PaymentModal
        setDialogOpen={setDialogOpen}
        isDialogOpen={isDialogOpen}
        amountPayable={totalPrice} // pass amount to pay into modal
        orgId={orgId}
        handleSubmit={handleSubmit}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        PAYMENT_METHODS={PAYMENT_METHODS}
        metaInfo={orderMetaInfo}
        email={email}
        handleNext={goNextPage}
        variant='order'
      />
    </PageLayout>
  )
}
